import { useEffect, useRef, useState } from "react";
import { Form, Input, Skeleton, TextArea } from "../../components/ui";
import {
  useAddActivity,
  useGetActivityById,
  useUpdateActivity,
} from "../../query/activityQuery";
import FormPhotoDrop from "../../components/FormPhotoDrop";
import swal from "../../utils/swal";
import { useNavigate, useParams } from "react-router-dom";
import { UPLOADS_URL } from "../../config/constants";
import "../teachers/addTeacher.css";
import "./activities.css";

function AddActivity() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addActivity, isPending: isAdding } = useAddActivity();
  const { mutate: updateActivity, isPending: isUpdating } = useUpdateActivity();
  const { data: activity, isLoading } = useGetActivityById(isEdit ? id : undefined);
  const intentRef = useRef("ACTIVE");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !activity) return;
    form.setFieldsValue({
      title: activity.title || "",
      description: activity.description || "",
    });
    if (activity.image) {
      setImagePreview(`${UPLOADS_URL}/${activity.image}`);
    }
    intentRef.current = activity.status === "INACTIVE" ? "INACTIVE" : "ACTIVE";
  }, [isEdit, activity, form]);

  const buildFormData = (values) => {
    const formObject = new FormData();
    if (imageFile) formObject.append("image", imageFile);
    formObject.append("title", values.title || "");
    formObject.append("description", values.description || "");
    formObject.append("status", intentRef.current === "INACTIVE" ? "INACTIVE" : "ACTIVE");
    return formObject;
  };

  const onFinish = (values) => {
    if (!isEdit && !imageFile) {
      swal("Oops!", "Please upload an image", "error");
      return;
    }

    const formObject = buildFormData(values);

    if (isEdit) {
      updateActivity(
        { id, data: formObject },
        {
          onSuccess: () => {
            swal("Success", "Activity updated successfully", "success");
            navigate(`/activities/${id}`);
          },
          onError: (error) => {
            swal("Error", error?.message || "Failed to update activity", "error");
          },
        }
      );
      return;
    }

    addActivity(
      { data: formObject },
      {
        onSuccess: (result) => {
          const payload = result?.data || result || {};
          const saved = payload.activity || payload;
          const drafted = intentRef.current === "INACTIVE";
          const lines = [drafted ? "Activity saved as inactive" : "Activity added successfully"];
          if (saved?.title) lines.push(`Title: ${saved.title}`);
          swal("Success", lines.join("\n"), "success");
          navigate("/activities");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to add activity", "error");
        },
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <section className="tf-card">
          <h2 className="tf-card-head">Activity Details</h2>
          <div className="tf-card-body">
            <FormPhotoDrop
              preview={imagePreview}
              required={!isEdit}
              label="Image"
              alt="Activity preview"
              variant="rect"
              hint="PNG, JPG, WEBP or SVG"
              onFile={(file) => {
                setImageFile(file);
                setImagePreview((prev) => {
                  if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(file);
                });
              }}
            />

            <div className="tf-field">
              <p className="tf-label">
                Title <span>*</span>
              </p>
              <Form.Item
                name="title"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input placeholder="Reading" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">Description</p>
              <Form.Item name="description" className="tf-item mb-0">
                <TextArea rows={5} placeholder="Describe this post category (e.g. story time, nap time)..." />
              </Form.Item>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button
            type="submit"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "INACTIVE";
            }}
          >
            {isMutating && intentRef.current === "INACTIVE" ? "Saving..." : "Save as Draft"}
          </button>
          <button
            type="submit"
            className="tf-btn is-primary"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "ACTIVE";
            }}
          >
            {isMutating && intentRef.current === "ACTIVE"
              ? "Saving..."
              : isEdit
              ? "Save"
              : "Submit"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddActivity;
